import { createLogger, Logger, LoggerLevel, RetryUtil } from '@quickplay/foundation';
import { NewRelicAnalyticsReporter } from './model/NewRelicAnalyticsReporter';
import { v4 as uuid } from 'uuid';
import { Provider } from '../Provider';
import { Player } from '@quickplay/player-interface';
import { AnalyticsReporterConfiguration } from '../../fl-analytics';
import {
  toApplicationReportingData,
  toNewRelicDeviceReportingData,
  toNewRelicUserReportingData,
} from '../util/DataUtil';
import { AnalyticsKeyTransformer } from '../util/AnalyticsKeyTransformer';

const TAG_NAME = 'FL_ANALYTICS';

declare global {
  interface Window {
    newrelic: any;
  }
}

export class NewRelicAnalyticsReporterImpl implements NewRelicAnalyticsReporter {
  private static instance: NewRelicAnalyticsReporter;
  private static readonly NR_AGENT_SCRIPT_RETRIES = 2;
  private static readonly NR_AGENT_SCRIPT_RETRY_DELAY_MS = 1_000;

  private logger?: Logger;
  private newrelicAgent: any;
  private globalMetadata: Record<string, any> = {};
  private initialized: boolean = false;

  constructor() {
    this.newrelicAgent = window.newrelic;
  }

  static getInstance(): NewRelicAnalyticsReporter {
    if (!NewRelicAnalyticsReporterImpl.instance) {
      NewRelicAnalyticsReporterImpl.instance = new NewRelicAnalyticsReporterImpl();
    }

    return NewRelicAnalyticsReporterImpl.instance;
  }

  get provider(): Provider {
    return Provider.NEWRELIC;
  }

  async initialize(configuration: AnalyticsReporterConfiguration): Promise<void> {
    this.logger = configuration.logger || createLogger(LoggerLevel.OFF, TAG_NAME);

    try {
      this.newrelicAgent = await RetryUtil.retry(
        () => {
          if (!window.newrelic) {
            throw new Error('New Relic agent not yet available');
          }
          return window.newrelic;
        },
        NewRelicAnalyticsReporterImpl.NR_AGENT_SCRIPT_RETRIES,
        NewRelicAnalyticsReporterImpl.NR_AGENT_SCRIPT_RETRY_DELAY_MS,
      );
    } catch (error) {
      this.logger?.error('Failed to initialize New Relic agent', error);
    }

    this.initialized = true;

    this.globalMetadata = {
      ...toApplicationReportingData(configuration.application),
      ...(configuration.device ? toNewRelicDeviceReportingData(configuration.device) : {}),
      ...(configuration.user ? toNewRelicUserReportingData(configuration.user) : {}),
    };

    if (!this.newrelicAgent) {
      this.logger?.warn('New Relic agent not found on the window object after retries.');
      return;
    }

    this.applyCustomAttributes(this.globalMetadata);
  }

  attachPlayer(_player: Player): void {
    // No-op for New Relic
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  reportMetadata(metadata: Record<string, any>): void {
    this.applyCustomAttributes(metadata);
  }

  reportEvent(eventName: string, eventMetadata: Record<string, any> = {}): void {
    if (!this.isInitialized()) {
      this.logger?.warn('New Relic instance not available');
      return;
    }

    const actionName = eventName?.toUpperCase() || 'UNKNOWN_EVENT';
    const eventUUID = uuid();

    // If user_id exists in metadata, apply it as a custom attribute
    if ('user_id' in eventMetadata) {
      this.applyCustomAttributes(eventMetadata);
    }

    const convertedMetadata = AnalyticsKeyTransformer.translateKeys({
      event_name: actionName,
      event_uuid: eventUUID,
      ...eventMetadata,
    });

    // send to NR agent
    this.newrelicAgent?.addPageAction(actionName, convertedMetadata);

    this.logger?.info(`Reported event to New Relic: ${actionName}`, convertedMetadata);
  }

  reset(): void {
    this.initialized = false;
    this.globalMetadata = {};
  }

  /** Safely applies custom attributes to New Relic agent */
  private applyCustomAttributes(attributes: Record<string, any>): void {
    if (!this.isInitialized()) {
      this.logger?.warn('New Relic agent not available');
      return;
    }

    const convertedMetadata = AnalyticsKeyTransformer.translateKeys({ ...attributes });
    for (const [key, value] of Object.entries(convertedMetadata)) {
      // Missing optional fields are undefined after metadata mapping. The
      // Browser agent only accepts scalars, or null to remove an attribute.
      // Preserve valid falsy values rather than checking truthiness.
      if (value !== null && typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') {
        continue;
      }

      try {
        this.newrelicAgent?.setCustomAttribute(key, value);
      } catch (e) {
        this.logger?.warn(`Failed to set attribute ${key}`, e);
      }
    }
  }
}
